import type { RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import type { SizeQueriesMap } from './useMeasureContainerWithResizeObserver.types';

const getBreakpointValues = <T extends string>(query: SizeQueriesMap<T>): number[] => {
  const values = new Set<number>();

  for (const key of Object.keys(query)) {
    const { minWidth } = query[key as T];

    if (minWidth !== undefined && minWidth > 0) {
      values.add(minWidth);
    }
  }

  return Array.from(values);
};

const isOnBreakpointThreshold = (width: number, breakpoints: number[]): boolean =>
  breakpoints.some((breakpoint) => Math.abs(width - breakpoint) <= 1);

const getMatchingSize = <T extends string>(query: SizeQueriesMap<T>, entry: ResizeObserverEntry): T | undefined => {
  for (const key of Object.keys(query)) {
    const { width, height } = entry.contentRect;
    const { maxHeight = Infinity, maxWidth = Infinity, minHeight = 0, minWidth = 0 } = query[key as T];

    if (height <= maxHeight && height >= minHeight && width <= maxWidth && width >= minWidth) {
      return key as T;
    }
  }
};

interface UseMeasureContainerWithResizeObserverOptions<T extends string> {
  readonly elementReference: RefObject<Element | null>;
  readonly query: SizeQueriesMap<T>;
  readonly includeAvailableWidth?: boolean;
}

/**
 * @internal Only for use within CoreUI.
 *
 * `useMeasureContainerWithResizeObserver` uses the `ResizeObserver` API to allow conditional child rendering based on the
 * component's container height or width.
 */

export const useMeasureContainerWithResizeObserver = <T extends string>({
  elementReference,
  query,
  includeAvailableWidth = false,
}: UseMeasureContainerWithResizeObserverOptions<T>): {
  readonly size: T | undefined;
  readonly availableWidth: number | undefined;
} => {
  const [size, setSize] = useState<T | undefined>();
  const [availableWidth, setAvailableWidth] = useState<number | undefined>();
  const previousSizeRef = useRef<T | undefined>();
  const lastReportedWidthRef = useRef<number | undefined>();

  useLayoutEffect(() => {
    if (elementReference.current) {
      const breakpointValues = includeAvailableWidth ? getBreakpointValues(query) : [];
      const resizeObserver = new ResizeObserver(([entry]) => {
        const newSize = getMatchingSize(query, entry);
        const sizeChanged = newSize !== previousSizeRef.current;

        if (sizeChanged) {
          previousSizeRef.current = newSize;
          setSize(newSize);
        }

        if (includeAvailableWidth) {
          const width = entry.contentRect.width;

          if (sizeChanged || isOnBreakpointThreshold(width, breakpointValues) || !lastReportedWidthRef.current) {
            lastReportedWidthRef.current = width;
            setAvailableWidth(width);
          }
        }
      });

      resizeObserver.observe(elementReference.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [elementReference, query, includeAvailableWidth]);

  return {
    size,
    availableWidth,
  };
};
